import { effect, inject, Injector } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import type { OnyxCustomAddress } from '@onyx/angular';
import {
  castArray,
  chain,
  cloneDeep,
  compact,
  orderBy,
  partition,
  uniq,
  uniqBy,
} from 'lodash';
import type { Observable } from 'rxjs';
import {
  catchError,
  forkJoin,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { v7 as uuidv7 } from 'uuid';
import type { TelematicsItem } from '../../../../common/interfaces/telematics/telematics-item';
import type { TelematicsOrderRoutePoint } from '../../../../common/interfaces/telematics/telematics-route-point';
import { TelematicsRoutePointStatus } from '../../../../common/interfaces/telematics/telematics-vehicle-point-status';
import type { Driver } from '../../../drivers/common/interfaces/driver';
import { DriversService } from '../../../drivers/common/services/drivers.service';
import type { Fleet } from '../../../fleet/common/interfaces/fleet';
import { FleetService } from '../../../fleet/common/services/fleet.service';
import type { Order } from '../../../orders/common/interfaces/order';
import { OrdersService } from '../../../orders/common/services/orders.service';
import type { MapSwapPanelForm } from '../../map/map-swap-panel/map-swap-panel.component';
import { SwapSetAction } from '../enums/swap/swap-set-action';
import { SwapUnassignPointReason } from '../enums/swap/swap-unassign-point-reason';
import { SwapVehicleType } from '../enums/swap/swap-vehicle-type';
import { SwapFormHelper } from '../helpers/swap/swap-form.helper';
import { SwapHelper } from '../helpers/swap/swap.helper';
import type { SwapFilters } from '../interfaces/swap/swap-filters';
import type { SwapRoutePoint } from '../interfaces/swap/swap-route-point';
import type { SwapSet, SwapSetData } from '../interfaces/swap/swap-set';
import type { SwapUnassignedPoint } from '../interfaces/swap/swap-unassigned-points';
import { MapStore } from './map.store';

export interface SwapState {
  showSwapPanel: boolean;

  isFetching: boolean;
  fetchingItemsCount: number;

  isDouble: boolean;
  isRecorded: boolean;

  filters: SwapFilters;
  address: OnyxCustomAddress | null;

  availableOrders: Order[];
  availableDrivers: Driver[];
  availableTrailers: Fleet[];

  vehicle: TelematicsItem | null;
  vehicleRoute: SwapRoutePoint[];
  vehicleInitialRoute: SwapRoutePoint[];
  vehicleSwapPointUuid: string;
  vehicleUnassignedPoints: SwapUnassignedPoint[];
  vehicleSemiTrailerPoints: SwapUnassignedPoint[];
  vehicleSet: SwapSet;

  otherVehicle: TelematicsItem | null;
  otherVehicleRoute: SwapRoutePoint[];
  otherVehicleInitialRoute: SwapRoutePoint[];
  otherVehicleSwapPointUuid: string;
  otherVehicleUnassignedPoints: SwapUnassignedPoint[];
  otherVehicleSemiTrailerPoints: SwapUnassignedPoint[];
  otherVehicleSet: SwapSet;
}

export interface SwapVehicleData {
  vehicle: TelematicsItem | null;
  route: SwapRoutePoint[];
  initialRoute: SwapRoutePoint[];
  swapPointUuid: string;
  unassignedPoints: SwapUnassignedPoint[];
  trailerPoints: SwapUnassignedPoint[];
  set: SwapSet;
}

export const SwapStore = signalStore(
  { providedIn: 'root' },
  withState<SwapState>(() => SwapHelper.getInitialState()),
  withProps(() => ({
    _injector: inject(Injector),
    _mapStore: inject(MapStore),
    _ordersService: inject(OrdersService),
    _driversService: inject(DriversService),
    _fleetService: inject(FleetService),
  })),
  withProps(() => ({
    swapPointClicked$: new Subject<void>(),
    _fetch$: new Subject<void>(),
    _vehicleChanged$: new Subject<void>(),
  })),
  withMethods((store) => ({
    updateFetchingState(isFetching: boolean): void {
      patchState(store, (state) => {
        const fetchingItemsCount =
          state.fetchingItemsCount + (isFetching ? 1 : -1);

        store._mapStore.setShowLoading(fetchingItemsCount > 0);

        return { fetchingItemsCount };
      });
    },

    _fetchData(): void {
      patchState(store, { isFetching: true });
      this.updateFetchingState(true);
      store._fetch$.next();
    },

    _getVehicleData(type: SwapVehicleType): SwapVehicleData {
      const isPrimary = type === SwapVehicleType.PRIMARY;
      return {
        vehicle: isPrimary ? store.vehicle() : store.otherVehicle(),
        route: isPrimary ? store.vehicleRoute() : store.otherVehicleRoute(),
        initialRoute: isPrimary
          ? store.vehicleInitialRoute()
          : store.otherVehicleInitialRoute(),
        swapPointUuid: isPrimary
          ? store.vehicleSwapPointUuid()
          : store.otherVehicleSwapPointUuid(),
        unassignedPoints: isPrimary
          ? store.vehicleUnassignedPoints()
          : store.otherVehicleUnassignedPoints(),
        trailerPoints: isPrimary
          ? store.vehicleSemiTrailerPoints()
          : store.otherVehicleSemiTrailerPoints(),
        set: isPrimary ? store.vehicleSet() : store.otherVehicleSet(),
      };
    },

    _patchFromVehicleData(
      type: SwapVehicleType,
      data: Partial<SwapVehicleData>,
    ): void {
      patchState(store, SwapHelper.mapToState(type, data));
    },
  })),
  withComputed((store) => ({
    isLoading: (): boolean => store.fetchingItemsCount() > 0,
    hasActivePoint: (): boolean =>
      [
        ...store.vehicleRoute(),
        ...(store.isDouble() ? store.otherVehicleRoute() : []),
      ].some((point) => SwapHelper.isActivePoint(point)),
    formValues: (): Partial<MapSwapPanelForm> =>
      SwapFormHelper.buildFormValues(
        store.address(),
        {
          vehicle: store.vehicle(),
          route: store.vehicleRoute(),
          unassigned: store.vehicleUnassignedPoints(),
          trailers: store.vehicleSemiTrailerPoints(),
          set: store.vehicleSet(),
        },
        {
          otherVehicle: store.otherVehicle(),
          otherRoute: store.otherVehicleRoute(),
          otherUnassigned: store.otherVehicleUnassignedPoints(),
          otherTrailers: store.otherVehicleSemiTrailerPoints(),
          otherSet: store.otherVehicleSet(),
        },
      ),
    hasErrors: (): boolean =>
      [store.vehicleSet(), store.otherVehicleSet()].some(
        (set: SwapSet): boolean =>
          Object.values(set).some((value) =>
            castArray(value).some((item) => !!item.errors?.length),
          ),
      ),
    primaryVehicleData: (): SwapVehicleData =>
      store._getVehicleData(SwapVehicleType.PRIMARY),
    otherVehicleData: (): SwapVehicleData =>
      store._getVehicleData(SwapVehicleType.SECONDARY),
  })),
  withComputed((store) => ({
    vehicleData: (): Record<SwapVehicleType, SwapVehicleData> => {
      return {
        [SwapVehicleType.PRIMARY]: store.primaryVehicleData(),
        [SwapVehicleType.SECONDARY]: store.otherVehicleData(),
      };
    },
  })),
  withMethods((store) => ({
    toggleShowSwapPanel(force?: boolean): void {
      patchState(store, (state) => ({
        showSwapPanel: force ?? !state.showSwapPanel,
      }));
    },

    setIsDouble(isDouble: boolean): void {
      if (store.isDouble() === isDouble) return;

      patchState(store, { isDouble });
      this.restore({ forceReset: true });
    },

    setIsRecorded(isRecorded: boolean): void {
      if (store.isRecorded() === isRecorded) return;

      patchState(store, { isRecorded });
      if (!isRecorded) return;

      const types = store.isDouble()
        ? [SwapVehicleType.PRIMARY, SwapVehicleType.SECONDARY]
        : [SwapVehicleType.PRIMARY];
      const hasSwapPointMoved = types.some((type) => {
        const { vehicle, route, swapPointUuid } = store.vehicleData()[type];
        return SwapHelper.hasSwapPointMoved(
          vehicle,
          route,
          store.isDouble(),
          swapPointUuid,
        );
      });
      if (!hasSwapPointMoved) return;

      this.restore({ forceReset: true });
    },

    setAddress(address: OnyxCustomAddress | null): void {
      patchState(store, { address });
    },

    updateFilters(filters: Partial<SwapFilters>): void {
      patchState(store, (state) => ({
        filters: { ...state.filters, ...filters },
      }));
    },

    setVehicle(vehicle: TelematicsItem | null): void {
      store._vehicleChanged$.next();

      const { route, initialRoute } = SwapHelper.getVehicleRoute(
        vehicle,
        store.isDouble(),
        store.vehicleSwapPointUuid(),
      );

      patchState(store, () => ({
        vehicle,
        vehicleRoute: route,
        vehicleInitialRoute: initialRoute,
        vehicleSet: SwapHelper.createSet(null, route),
        vehicleSwapPointUuid: uuidv7(),
        address: null,
      }));

      store._fetchData();
    },

    updateOtherVehicle(otherVehicle: TelematicsItem | null): void {
      if (store.otherVehicle()?.uuid === otherVehicle?.uuid) return;

      store._vehicleChanged$.next();

      if (!otherVehicle) {
        this.restoreOtherVehicle();
      } else {
        const { route, initialRoute } = SwapHelper.getVehicleRoute(
          otherVehicle,
          store.isDouble(),
          store.otherVehicleSwapPointUuid(),
        );

        patchState(store, () => ({
          otherVehicle,
          otherVehicleRoute: route,
          otherVehicleInitialRoute: initialRoute,
          otherVehicleSet: SwapHelper.createSet(null, route),
          otherVehicleSwapPointUuid: uuidv7(),
        }));
      }

      this.restore({ type: SwapVehicleType.SECONDARY });
      store._fetchData();
    },

    getOrderPoints(type: SwapVehicleType, orderUuid: string): SwapRoutePoint[] {
      return store
        .vehicleData()
        [type].route.filter(
          ({ isSwapPoint, point }) =>
            !isSwapPoint && point.order.uuid === orderUuid,
        );
    },

    _resetItem(type: SwapVehicleType): void {
      const { vehicle, swapPointUuid, set } = store.vehicleData()[type];
      const { route } = SwapHelper.getVehicleRoute(
        vehicle,
        store.isDouble(),
        swapPointUuid,
      );
      const initialState = SwapHelper.getInitialState();
      const isPrimary = type === SwapVehicleType.PRIMARY;

      store._patchFromVehicleData(type, {
        route,
        unassignedPoints: isPrimary
          ? initialState.vehicleUnassignedPoints
          : initialState.otherVehicleUnassignedPoints,
        trailerPoints: isPrimary
          ? initialState.vehicleSemiTrailerPoints
          : initialState.otherVehicleSemiTrailerPoints,
        set: SwapHelper.createSet(
          set.vehicle.value,
          route,
          store.availableDrivers(),
          store.availableTrailers(),
          store.availableOrders(),
        ),
      });
    },

    restoreOtherVehicle(): void {
      const initialState = SwapHelper.getInitialState();

      patchState(store, () => ({
        otherVehicle: null,
        otherVehicleRoute: initialState.otherVehicleRoute,
        otherVehicleInitialRoute: initialState.otherVehicleInitialRoute,
        otherVehicleSet: initialState.otherVehicleSet,
        otherVehicleUnassignedPoints: initialState.otherVehicleUnassignedPoints,
        otherVehicleSemiTrailerPoints:
          initialState.otherVehicleSemiTrailerPoints,
      }));
    },

    reset() {
      const initialState = SwapHelper.getInitialState();

      this.restoreOtherVehicle();
      patchState(store, {
        vehicle: null,
        vehicleRoute: initialState.vehicleRoute,
        vehicleInitialRoute: initialState.vehicleInitialRoute,
        vehicleSet: initialState.vehicleSet,
        vehicleSwapPointUuid: initialState.vehicleSwapPointUuid,
        vehicleUnassignedPoints: initialState.vehicleUnassignedPoints,
        vehicleSemiTrailerPoints: initialState.vehicleSemiTrailerPoints,
      });
    },

    restore(data?: { type?: SwapVehicleType; forceReset?: boolean }): void {
      const { type = SwapVehicleType.PRIMARY, forceReset = false } = data ?? {};
      const shouldResetAll =
        forceReset ||
        SwapHelper.shouldResetAllVehicles(
          store.primaryVehicleData().set,
          store.otherVehicleData().set,
          store.isDouble(),
        );

      if (shouldResetAll) {
        this._resetItem(SwapVehicleType.PRIMARY);
        this._resetItem(SwapVehicleType.SECONDARY);
        return;
      }

      this._resetItem(type);
    },

    unassignPoint(
      type: SwapVehicleType,
      point: TelematicsOrderRoutePoint,
      reason: SwapUnassignPointReason,
    ): void {
      const { route, unassignedPoints } = store.vehicleData()[type];

      const newUnassignedPoints = SwapHelper.getRoutePoints(route).filter(
        (routePoint) =>
          routePoint.status !== TelematicsRoutePointStatus.COMPLETED &&
          SwapHelper.isMatchingReason(routePoint, point, reason),
      );
      if (!newUnassignedPoints.length) return;

      const newUnassignedPointsUuid = new Set(
        newUnassignedPoints.map(({ point }) => point.uuid),
      );

      store._patchFromVehicleData(type, {
        route: route.filter(
          (item) => !newUnassignedPointsUuid.has(SwapHelper.getPointUuid(item)),
        ),
        unassignedPoints: [
          ...unassignedPoints,
          ...newUnassignedPoints.map((point) => ({ point, reason })),
        ],
      });
    },

    restorePoint(
      type: SwapVehicleType,
      point: TelematicsOrderRoutePoint,
      reason: SwapUnassignPointReason,
    ): void {
      const { route, unassignedPoints, vehicle, swapPointUuid } =
        store.vehicleData()[type];

      const restoredPoints = unassignedPoints.filter(
        (unassignedPoint) =>
          unassignedPoint.point.status !==
            TelematicsRoutePointStatus.COMPLETED &&
          SwapHelper.isMatchingReason(unassignedPoint.point, point, reason),
      );
      if (!restoredPoints.length) return;

      const restoredPointsUuid = new Set(
        restoredPoints.map(({ point }) => point.point.uuid),
      );
      const initialRouteOrder = SwapHelper.getVehicleInitialRouteOrderMap(
        vehicle,
        store.isDouble(),
        swapPointUuid,
      );

      store._patchFromVehicleData(type, {
        route: SwapHelper.restorePointsByInitialMap(
          route,
          restoredPoints.map(({ point }) => ({ isSwapPoint: false, point })),
          initialRouteOrder,
        ),
        unassignedPoints: unassignedPoints.filter(
          ({ point }) => !restoredPointsUuid.has(point.point.uuid),
        ),
      });
    },

    /* Drivers */

    updateDriverAction(
      type: SwapVehicleType,
      driverUuid: string,
      action: SwapSetAction,
    ): void {
      const data = store.vehicleData()[type];
      const driver = data.set.drivers.find(
        ({ value }) => value?.uuid === driverUuid,
      );
      if (!driver) return;

      const previousAction = driver.action;
      if (action === previousAction) return;

      switch (action) {
        case SwapSetAction.TRANSFER:
          return this._moveDriver(type, driver, SwapSetAction.TRANSFER);

        case SwapSetAction.DELETE:
          return this._deleteDriver(type, driver);

        case SwapSetAction.NONE:
          if (previousAction === SwapSetAction.TRANSFER) {
            return this._moveDriver(type, driver, SwapSetAction.NONE);
          }
          break;
      }

      const updatedDrivers = data.set.drivers.map((item) =>
        item.value?.uuid === driverUuid ? { ...item, action } : item,
      );

      store._patchFromVehicleData(type, {
        set: {
          ...data.set,
          drivers: SwapHelper.syncDrivers(updatedDrivers, data.set),
        },
      });
    },

    addDriver(type: SwapVehicleType, driver: Driver): void {
      const { set } = store.vehicleData()[type];
      const updatedDrivers: SwapSetData<Driver>[] = [
        ...set.drivers,
        { value: driver, action: SwapSetAction.NONE, isManuallyAdded: true },
      ];

      store._patchFromVehicleData(type, {
        set: {
          ...set,
          drivers: SwapHelper.syncDrivers(updatedDrivers, set),
        },
      });
    },

    _moveDriver(
      sourceType: SwapVehicleType,
      driver: SwapSetData<Driver>,
      action: SwapSetAction,
    ): void {
      if (!driver.value) return;

      const targetType = SwapHelper.getOtherType(sourceType);
      const sourceData = store.vehicleData()[sourceType];
      const targetData = store.vehicleData()[targetType];
      const driverUuid = driver.value.uuid;

      const updatedFromDrivers = sourceData.set.drivers.filter(
        ({ value }) => value?.uuid !== driverUuid,
      );
      const updatedToDrivers = [
        ...targetData.set.drivers.filter(
          ({ value }) => value?.uuid !== driverUuid,
        ),
        { ...driver, action },
      ];

      store._patchFromVehicleData(sourceType, {
        set: {
          ...sourceData.set,
          drivers: SwapHelper.syncDrivers(updatedFromDrivers, sourceData.set),
        },
      });

      store._patchFromVehicleData(targetType, {
        set: {
          ...targetData.set,
          drivers: SwapHelper.syncDrivers(updatedToDrivers, targetData.set),
        },
      });
    },

    _deleteDriver(type: SwapVehicleType, driver: SwapSetData<Driver>): void {
      if (!driver.value) return;

      const data = store.vehicleData()[type];
      const driverUuid = driver.value.uuid;

      const updatedDrivers = data.set.drivers.filter(
        ({ value }) => value?.uuid !== driverUuid,
      );

      store._patchFromVehicleData(type, {
        set: {
          ...data.set,
          drivers: SwapHelper.syncDrivers(updatedDrivers, data.set),
        },
      });
    },

    /* Orders */

    updateOrderAction(
      type: SwapVehicleType,
      orderUuid: string,
      action: SwapSetAction,
    ): void {
      const { set } = store.vehicleData()[type];
      const order = set.activeOrders.find(
        ({ value }) => value?.uuid === orderUuid,
      );
      if (!order) return;

      const previousAction = order.action;
      if (action === previousAction) return;

      switch (action) {
        case SwapSetAction.UNLOAD_IN_POINT:
          if (previousAction === SwapSetAction.DROP_OFF_ON_SEMI_TRAILER) {
            this._moveOrderPointsToUnassignedPoints(
              type,
              orderUuid,
              'unassigned',
            );
            break;
          }

          if (previousAction === SwapSetAction.TRANSFER) {
            this._revertTransferredOrder(type, orderUuid);

            return this.updateOrderAction(
              SwapHelper.getOtherType(type),
              orderUuid,
              SwapSetAction.UNLOAD_IN_POINT,
            );
          }

          this._unloadActiveOrder(type, orderUuid);
          break;

        case SwapSetAction.DROP_OFF_ON_SEMI_TRAILER:
          if (previousAction === SwapSetAction.UNLOAD_IN_POINT) {
            this._moveOrderPointsToUnassignedPoints(type, orderUuid, 'trailer');
          }
          break;

        case SwapSetAction.TRANSFER:
          this.transferOrder(type, orderUuid);
          break;

        case SwapSetAction.NONE: {
          if (previousAction === SwapSetAction.UNLOAD_IN_POINT) {
            this._restoreUnloadedOrder(type, orderUuid);
          } else if (previousAction === SwapSetAction.TRANSFER) {
            this._revertTransferredOrder(type, orderUuid);
          }
          break;
        }
      }

      const data = store.vehicleData()[type];
      let updatedOrders = data.set.activeOrders.map((item) =>
        item.value?.uuid === orderUuid ? { ...item, action } : item,
      );

      const hasOrderInSet = updatedOrders.some(
        (item) => item.value?.uuid === orderUuid,
      );
      if (!hasOrderInSet && action === SwapSetAction.UNLOAD_IN_POINT) {
        const orderValue = store
          .availableOrders()
          .find(({ uuid }) => uuid === orderUuid);

        if (orderValue) {
          updatedOrders = [
            ...updatedOrders,
            { value: orderValue, action: SwapSetAction.UNLOAD_IN_POINT },
          ];
        }
      }

      store._patchFromVehicleData(type, {
        set: {
          ...data.set,
          activeOrders: SwapHelper.syncActiveOrders(
            updatedOrders,
            data.route,
            store.availableOrders(),
            data.set,
          ),
        },
      });
    },

    _moveOrderPointsToUnassignedPoints(
      type: SwapVehicleType,
      orderUuid: string,
      unassignedType: 'trailer' | 'unassigned',
    ): void {
      const { trailerPoints, unassignedPoints, vehicle, swapPointUuid } =
        store.vehicleData()[type];

      const isTrailerTarget = unassignedType === 'trailer';
      const sourcePoints = isTrailerTarget ? unassignedPoints : trailerPoints;

      const movedPoints = sourcePoints
        .filter(({ point }) => point.order?.uuid === orderUuid)
        .map(({ point }) => ({
          point,
          reason: isTrailerTarget
            ? SwapUnassignPointReason.SEMI_TRAILER
            : SwapUnassignPointReason.POINT,
        }));
      if (!movedPoints.length) return;

      const movePointsUuid = new Set(
        movedPoints.map(({ point }) => point.point.uuid),
      );

      const initialRouteOrderMap = SwapHelper.getVehicleInitialRouteOrderMap(
        vehicle,
        store.isDouble(),
        swapPointUuid,
      );

      const sort = (list: SwapUnassignedPoint[]) =>
        orderBy(
          list,
          (item) => initialRouteOrderMap.get(item.point.point.uuid) ?? Infinity,
          'asc',
        );

      store._patchFromVehicleData(type, {
        trailerPoints: sort(
          isTrailerTarget
            ? [...trailerPoints, ...movedPoints]
            : trailerPoints.filter(
                ({ point }) => !movePointsUuid.has(point.point.uuid),
              ),
        ),
        unassignedPoints: sort(
          isTrailerTarget
            ? unassignedPoints.filter(
                ({ point }) => !movePointsUuid.has(point.point.uuid),
              )
            : [...unassignedPoints, ...movedPoints],
        ),
      });
    },

    transferOrder(sourceType: SwapVehicleType, orderUuid: string): void {
      const targetType = SwapHelper.getOtherType(sourceType);
      const sourceData = store.vehicleData()[sourceType];
      const targetData = store.vehicleData()[targetType];
      if (!targetData.vehicle) return;

      const sourceSwapIndex = SwapHelper.getSwapPointIndex(sourceData.route);

      const isMatchingOrder = (point: SwapRoutePoint | SwapUnassignedPoint) =>
        SwapHelper.getPointOrderUuid(point) === orderUuid;

      const transferredPoints = [
        ...sourceData.route.slice(sourceSwapIndex + 1).filter(isMatchingOrder),
        ...[...sourceData.unassignedPoints, ...sourceData.trailerPoints]
          .filter(isMatchingOrder)
          .map(({ point }): SwapRoutePoint => ({ isSwapPoint: false, point })),
      ];
      if (!transferredPoints.length) return;

      const transferredPointsUuid = new Set(
        transferredPoints.map((point) => SwapHelper.getPointUuid(point)),
      );

      const newSourceRoute = sourceData.route.filter(
        (item) =>
          item.isSwapPoint || !transferredPointsUuid.has(item.point.point.uuid),
      );
      const newSourceUnassignedPoints = sourceData.unassignedPoints.filter(
        ({ point }) => !transferredPointsUuid.has(point.point.uuid),
      );
      const newSourceTrailerPoints = sourceData.trailerPoints.filter(
        ({ point }) => !transferredPointsUuid.has(point.point.uuid),
      );

      const newTransferInsertIndex = SwapHelper.getTransferInsertIndex(
        targetData.route,
      );
      const newTargetRoute = cloneDeep(targetData.route);
      newTargetRoute.splice(newTransferInsertIndex, 0, ...transferredPoints);

      const newTargetOrders = cloneDeep(targetData.set.activeOrders);

      const transferredOrder = store
        .availableOrders()
        .find((order) => order.uuid === orderUuid);
      if (transferredOrder) {
        const orderIndex = newTargetOrders.findIndex(
          (order) => order.value?.uuid === orderUuid,
        );

        if (orderIndex !== -1) {
          newTargetOrders.with(orderIndex, {
            ...newTargetOrders[orderIndex],
            action: SwapSetAction.TRANSFER,
          });
        } else {
          newTargetOrders.push({
            value: transferredOrder,
            action: SwapSetAction.TRANSFER,
          });
        }
      }

      store._patchFromVehicleData(sourceType, {
        route: newSourceRoute,
        unassignedPoints: newSourceUnassignedPoints,
        trailerPoints: newSourceTrailerPoints,
        set: {
          ...sourceData.set,
          activeOrders: SwapHelper.syncActiveOrders(
            sourceData.set.activeOrders.map((order) =>
              order.value?.uuid === orderUuid
                ? { ...order, action: SwapSetAction.NONE }
                : order,
            ),
            newSourceRoute,
            store.availableOrders(),
            sourceData.set,
          ),
        },
      });

      store._patchFromVehicleData(targetType, {
        route: newTargetRoute,
        set: {
          ...targetData.set,
          activeOrders: SwapHelper.syncActiveOrders(
            newTargetOrders,
            newTargetRoute,
            store.availableOrders(),
            targetData.set,
          ),
        },
      });
    },

    _revertTransferredOrder(
      targetType: SwapVehicleType,
      orderUuid: string,
    ): void {
      const targetData = store.vehicleData()[targetType];
      const revertedPoints = targetData.route.filter(
        (point) => SwapHelper.getPointOrderUuid(point) === orderUuid,
      );
      if (!revertedPoints.length) return;

      const sourceType = SwapHelper.getOtherType(targetType);
      const sourceData = store.vehicleData()[sourceType];

      const revertedPointsUuid = new Set(
        revertedPoints.map((point) => SwapHelper.getPointUuid(point)),
      );
      const newTargetRoute = targetData.route.filter(
        (point) =>
          point.isSwapPoint ||
          !revertedPointsUuid.has(SwapHelper.getPointUuid(point)),
      );

      store._patchFromVehicleData(targetType, {
        route: newTargetRoute,
        set: {
          ...targetData.set,
          activeOrders: SwapHelper.syncActiveOrders(
            targetData.set.activeOrders.map((order) =>
              order.value?.uuid === orderUuid
                ? { ...order, action: SwapSetAction.NONE }
                : order,
            ),
            newTargetRoute,
            store.availableOrders(),
            targetData.set,
          ),
        },
      });

      const newSourceRoute = SwapHelper.restorePointsByInitialMap(
        sourceData.route,
        revertedPoints,
        SwapHelper.getVehicleInitialRouteOrderMap(
          sourceData.vehicle,
          store.isDouble(),
          sourceData.swapPointUuid,
        ),
      );

      store._patchFromVehicleData(sourceType, {
        route: newSourceRoute,
        set: {
          ...sourceData.set,
          activeOrders: SwapHelper.syncActiveOrders(
            sourceData.set.activeOrders,
            newSourceRoute,
            store.availableOrders(),
            sourceData.set,
          ),
        },
      });
    },

    _unloadActiveOrder(type: SwapVehicleType, orderUuid: string): void {
      const { route, unassignedPoints } = store.vehicleData()[type];

      const swapIndex = SwapHelper.getSwapPointIndex(route);
      const unloadedPoints = route
        .slice(swapIndex + 1)
        .filter(
          ({ isSwapPoint, point }) =>
            !isSwapPoint && point.order.uuid === orderUuid,
        );
      if (!unloadedPoints.length) return;

      const unloadedPointsUuid = new Set(
        unloadedPoints.map((point) => SwapHelper.getPointUuid(point)),
      );

      store._patchFromVehicleData(type, {
        route: route.filter(
          (point) =>
            point.isSwapPoint ||
            !unloadedPointsUuid.has(SwapHelper.getPointUuid(point)),
        ),
        unassignedPoints: [
          ...unassignedPoints,
          ...unloadedPoints
            .filter((point) => !point.isSwapPoint)
            .map(({ point }) => ({
              point,
              reason: SwapUnassignPointReason.POINT,
            })),
        ],
      });
    },

    _restoreUnloadedOrder(type: SwapVehicleType, orderUuid: string): void {
      const { unassignedPoints, route, vehicle, swapPointUuid } =
        store.vehicleData()[type];

      const restoredPoints = unassignedPoints.filter(
        ({ point }) => point.order.uuid === orderUuid,
      );
      if (!restoredPoints.length) return;

      const restoredPointsUuid = new Set(
        restoredPoints.map((point) => SwapHelper.getPointUuid(point)),
      );

      store._patchFromVehicleData(type, {
        route: SwapHelper.restorePointsByInitialMap(
          route,
          restoredPoints.map(({ point }) => ({ isSwapPoint: false, point })),
          SwapHelper.getVehicleInitialRouteOrderMap(
            vehicle,
            store.isDouble(),
            swapPointUuid,
          ),
        ),
        unassignedPoints: unassignedPoints.filter(
          (point) => !restoredPointsUuid.has(SwapHelper.getPointUuid(point)),
        ),
      });
    },

    /* Trailers */

    updateTrailerAction(type: SwapVehicleType, action: SwapSetAction): void {
      const { set } = store.vehicleData()[type];
      const previousAction = set.trailer?.action ?? SwapSetAction.NONE;
      if (action === previousAction) return;

      let holdCurrentState = false;

      switch (action) {
        case SwapSetAction.NONE:
          switch (previousAction) {
            case SwapSetAction.DROP_OFF_IN_POINT:
              this._restoreTrailerDroppedPoints(type);
              break;

            case SwapSetAction.TRANSFER:
              this._revertTransferredTrailer(type);
              holdCurrentState = true;
              break;

            case SwapSetAction.SWAP:
              this._swapTrailer(type, SwapSetAction.NONE);
              break;
          }
          break;

        case SwapSetAction.DROP_OFF_IN_POINT:
          this._dropOffTrailer(type);
          break;

        case SwapSetAction.TRANSFER:
          this._transferTrailer(type);
          break;

        case SwapSetAction.SWAP:
          this._swapTrailer(type, SwapSetAction.SWAP);
          break;

        case SwapSetAction.DELETE:
          this._deleteTrailer(type);
          holdCurrentState = true;
          break;
      }

      if (holdCurrentState) return;

      const data = store.vehicleData()[type];
      store._patchFromVehicleData(type, {
        set: {
          ...data.set,
          trailer: { ...data.set.trailer, action },
        },
      });
    },

    addTrailer(type: SwapVehicleType, trailer: Fleet): void {
      const { set, route } = store.vehicleData()[type];
      const updatedTrailer: SwapSetData<Fleet> = {
        ...set.trailer,
        value: trailer,
        action: SwapSetAction.NONE,
        isManuallyAdded: true,
      };

      const updatedSet = {
        ...set,
        trailer: updatedTrailer,
      };

      store._patchFromVehicleData(type, {
        set: {
          ...updatedSet,
          activeOrders: SwapHelper.syncActiveOrders(
            set.activeOrders,
            route,
            store.availableOrders(),
            updatedSet,
          ),
        },
      });
    },

    _dropOffTrailer(type: SwapVehicleType): void {
      const { route, trailerPoints, set } = store.vehicleData()[type];
      const swapIndex = SwapHelper.getSwapPointIndex(route);

      const routeBeforeSwap = route.slice(0, swapIndex + 1);
      const routeAfterSwap = route.slice(swapIndex + 1);
      if (!routeAfterSwap.length) return;

      const transferredOrdersUuid = new Set(
        chain(set.activeOrders)
          .filter(({ action }) => action === SwapSetAction.TRANSFER)
          .map(({ value }) => value?.uuid)
          .compact()
          .value(),
      );

      const [transferredPoints, nonTransferredPoints] = partition(
        routeAfterSwap.filter((point) => !point.isSwapPoint),
        (item) => {
          const uuid = SwapHelper.getPointOrderUuid(item);
          return uuid && transferredOrdersUuid.has(uuid);
        },
      );

      const droppedOrdersUuid = new Set(
        chain(nonTransferredPoints)
          .map((point) => SwapHelper.getPointOrderUuid(point))
          .compact()
          .value(),
      );

      const existingPointsUuid = new Set(
        trailerPoints.map((point) => SwapHelper.getPointUuid(point)),
      );

      const newTrailerUnassignedPoints = chain(nonTransferredPoints)
        .map((point) => point.point)
        .filter((point) => !existingPointsUuid.has(point.point.uuid))
        .map((point) => ({
          point,
          reason: SwapUnassignPointReason.SEMI_TRAILER,
        }))
        .value();

      const newRoute = cloneDeep([...routeBeforeSwap, ...transferredPoints]);
      const updatedSet = {
        ...set,
        trailer: { ...set.trailer, action: SwapSetAction.DROP_OFF_IN_POINT },
      };

      store._patchFromVehicleData(type, {
        route: newRoute,
        trailerPoints: [...trailerPoints, ...newTrailerUnassignedPoints],
        set: {
          ...updatedSet,
          activeOrders: SwapHelper.syncActiveOrders(
            set.activeOrders.map((order) => {
              const uuid = order.value?.uuid;

              return uuid &&
                droppedOrdersUuid.has(uuid) &&
                order.action === SwapSetAction.NONE
                ? { ...order, action: SwapSetAction.DROP_OFF_ON_SEMI_TRAILER }
                : order;
            }),
            newRoute,
            store.availableOrders(),
            updatedSet,
          ),
        },
      });
    },

    _restoreTrailerDroppedPoints(type: SwapVehicleType): void {
      const data = store.vehicleData()[type];
      const { trailerPoints, vehicle, swapPointUuid, route, set } = data;

      const [restoredPoints, remainingTrailerPoints] = partition(
        trailerPoints,
        ({ reason, point }) =>
          reason === SwapUnassignPointReason.SEMI_TRAILER &&
          point.status !== TelematicsRoutePointStatus.COMPLETED,
      );

      const restoredRoutePoints: SwapRoutePoint[] = restoredPoints.map(
        ({ point }) => ({
          isSwapPoint: false,
          point,
        }),
      );

      const initialRouteOrderMap = SwapHelper.getVehicleInitialRouteOrderMap(
        vehicle,
        store.isDouble(),
        swapPointUuid,
      );

      const transferredOrdersUuid = new Set(
        chain(set.activeOrders)
          .filter(({ action }) => action === SwapSetAction.TRANSFER)
          .map(({ value }) => value?.uuid)
          .compact()
          .value(),
      );

      const [transferredPoints, remainingPoints] = partition(
        route.map((point, index) => ({ point, index })),
        ({ point }) => {
          const uuid = SwapHelper.getPointOrderUuid(point);
          return uuid && transferredOrdersUuid.has(uuid);
        },
      );

      const restoredRoute = SwapHelper.restorePointsByInitialMap(
        remainingPoints.map(({ point }) => point),
        restoredRoutePoints,
        initialRouteOrderMap,
      );

      const sortedTransferredPoints = orderBy(
        transferredPoints,
        ({ index }) => index,
        'desc',
      );
      for (const { point, index } of sortedTransferredPoints) {
        const insertIndex = Math.min(index, restoredRoute.length);
        restoredRoute.splice(insertIndex, 0, point);
      }

      const restoredOrdersUuid = new Set(
        chain(restoredPoints)
          .map((point) => SwapHelper.getPointOrderUuid(point))
          .compact()
          .value(),
      );

      const updatedSet = {
        ...set,
        trailer: { ...set.trailer, action: SwapSetAction.NONE },
      };

      store._patchFromVehicleData(type, {
        route: restoredRoute,
        trailerPoints: remainingTrailerPoints,
        set: {
          ...updatedSet,
          activeOrders: SwapHelper.syncActiveOrders(
            set.activeOrders.map((order) => {
              const uuid = order.value?.uuid;
              const shouldResetAction =
                uuid &&
                restoredOrdersUuid.has(uuid) &&
                [
                  SwapSetAction.DROP_OFF_ON_SEMI_TRAILER,
                  SwapSetAction.DROP_OFF_IN_POINT,
                ].includes(order.action);

              return shouldResetAction
                ? { ...order, action: SwapSetAction.NONE }
                : order;
            }),
            restoredRoute,
            store.availableOrders(),
            updatedSet,
          ),
        },
      });
    },

    _transferTrailer(sourceType: SwapVehicleType): void {
      const targetType = SwapHelper.getOtherType(sourceType);
      const sourceData = store.vehicleData()[sourceType];
      const targetData = store.vehicleData()[targetType];
      if (!targetData.vehicle || !sourceData.set.trailer?.value) return;

      const sourceSwapIndex = SwapHelper.getSwapPointIndex(sourceData.route);
      const transferredPoints = sourceData.route.slice(sourceSwapIndex + 1);

      const transferredPointsUuid = new Set(
        transferredPoints.map((point) => SwapHelper.getPointUuid(point)),
      );
      const transferredOrdersUuid = new Set(
        chain(transferredPoints)
          .filter((point) => !point.isSwapPoint)
          .map((point) => SwapHelper.getPointOrderUuid(point))
          .compact()
          .value(),
      );

      const newSourceRoute = sourceData.route.filter(
        (point) => !transferredPointsUuid.has(SwapHelper.getPointUuid(point)),
      );

      const targetRoutePointsUuid = new Set(
        targetData.route.map((point) => SwapHelper.getPointUuid(point)),
      );
      const uniqueTransferredPoints = transferredPoints.filter(
        (point) => !targetRoutePointsUuid.has(SwapHelper.getPointUuid(point)),
      );
      const newTargetRoute = [...targetData.route, ...uniqueTransferredPoints];

      const newSourceOrders = sourceData.set.activeOrders.map((order) => {
        const uuid = order.value?.uuid;
        return uuid && transferredOrdersUuid.has(uuid)
          ? { ...order, action: SwapSetAction.NONE }
          : order;
      });

      const newTargetOrders = targetData.set.activeOrders.map((order) => {
        const uuid = order.value?.uuid;
        return uuid && transferredOrdersUuid.has(uuid)
          ? { ...order, action: SwapSetAction.TRANSFER }
          : order;
      });

      for (const uuid of transferredOrdersUuid) {
        if (newTargetOrders.some(({ value }) => value?.uuid === uuid)) {
          continue;
        }

        const sourceOrder = sourceData.set.activeOrders.find(
          ({ value }) => value?.uuid === uuid,
        );
        if (!sourceOrder) continue;

        newTargetOrders.push({
          ...sourceOrder,
          action: SwapSetAction.TRANSFER,
        });
      }

      const updatedSourceSet = {
        ...sourceData.set,
        trailer: {
          ...sourceData.set.trailer,
          action: SwapSetAction.TRANSFER,
          value: null,
        },
      };

      store._patchFromVehicleData(sourceType, {
        route: newSourceRoute,
        set: {
          ...updatedSourceSet,
          activeOrders: SwapHelper.syncActiveOrders(
            newSourceOrders,
            newSourceRoute,
            store.availableOrders(),
            updatedSourceSet,
          ),
        },
      });

      const targetDroppedTrailer =
        targetData.set.trailer.action === SwapSetAction.DROP_OFF_IN_POINT
          ? targetData.set.trailer.value
          : null;

      const updatedTargetSet = {
        ...targetData.set,
        trailer: {
          ...targetData.set.trailer,
          action: SwapSetAction.TRANSFER,
          initialData: targetDroppedTrailer,
          value: sourceData.set.trailer.value,
        },
      };

      store._patchFromVehicleData(targetType, {
        route: newTargetRoute,
        set: {
          ...updatedTargetSet,
          activeOrders: SwapHelper.syncActiveOrders(
            newTargetOrders,
            newTargetRoute,
            store.availableOrders(),
            updatedTargetSet,
          ),
        },
      });
    },

    _revertTransferredTrailer(targetType: SwapVehicleType): void {
      const sourceType = SwapHelper.getOtherType(targetType);
      const targetData = store.vehicleData()[targetType];
      const sourceData = store.vehicleData()[sourceType];

      const transferredTrailer = targetData.set.trailer;
      if (!transferredTrailer.value) return;

      const sourceInitialRouteOrderMap =
        SwapHelper.getVehicleInitialRouteOrderMap(
          sourceData.vehicle,
          store.isDouble(),
          sourceData.swapPointUuid,
        );

      const revertedPoints = targetData.route.filter(
        (point) =>
          !point.isSwapPoint &&
          sourceInitialRouteOrderMap.has(SwapHelper.getPointUuid(point)),
      );
      const revertedPointsUuid = new Set(
        revertedPoints.map((point) => SwapHelper.getPointUuid(point)),
      );

      const newTargetRoute = targetData.route.filter(
        (point) => !revertedPointsUuid.has(SwapHelper.getPointUuid(point)),
      );
      const newSourceRoute = SwapHelper.restorePointsByInitialMap(
        sourceData.route,
        revertedPoints,
        sourceInitialRouteOrderMap,
      );

      const transferredOrdersUuid = new Set(
        chain(targetData.set.activeOrders)
          .filter(({ action }) => action === SwapSetAction.TRANSFER)
          .map(({ value }) => value?.uuid)
          .compact()
          .value(),
      );

      const newTargetOrders = targetData.set.activeOrders.map((order) =>
        transferredOrdersUuid.has(order.value?.uuid ?? '')
          ? { ...order, action: SwapSetAction.NONE }
          : order,
      );

      const newSourceOrders = cloneDeep(sourceData.set.activeOrders);
      for (const uuid of transferredOrdersUuid) {
        const sourceIndex = newSourceOrders.findIndex(
          ({ value }) => value?.uuid === uuid,
        );

        if (sourceIndex !== -1) {
          newSourceOrders[sourceIndex] = {
            ...newSourceOrders[sourceIndex],
            action: SwapSetAction.NONE,
          };
        } else {
          const originalOrder = targetData.set.activeOrders.find(
            ({ value }) => value?.uuid === uuid,
          );
          if (originalOrder) {
            newSourceOrders.push({
              ...originalOrder,
              action: SwapSetAction.NONE,
            });
          }
        }
      }

      const updatedTargetSet = {
        ...targetData.set,
        trailer: {
          ...transferredTrailer,
          action: transferredTrailer.initialData
            ? SwapSetAction.DROP_OFF_IN_POINT
            : SwapSetAction.NONE,
          initialData: null,
          value: transferredTrailer.initialData ?? null,
        },
      };

      store._patchFromVehicleData(targetType, {
        route: newTargetRoute,
        set: {
          ...updatedTargetSet,
          activeOrders: SwapHelper.syncActiveOrders(
            newTargetOrders,
            newTargetRoute,
            store.availableOrders(),
            updatedTargetSet,
          ),
        },
      });

      const updatedSourceSet = {
        ...sourceData.set,
        trailer: {
          ...sourceData.set.trailer,
          action: SwapSetAction.NONE,
          value: sourceData.set.trailer.value ?? transferredTrailer.value,
        },
      };

      store._patchFromVehicleData(sourceType, {
        route: newSourceRoute,
        set: {
          ...updatedSourceSet,
          activeOrders: SwapHelper.syncActiveOrders(
            newSourceOrders,
            newSourceRoute,
            store.availableOrders(),
            updatedSourceSet,
          ),
        },
      });
    },

    _swapTrailer(sourceType: SwapVehicleType, action: SwapSetAction): void {
      const targetType = SwapHelper.getOtherType(sourceType);
      const sourceData = store.vehicleData()[sourceType];
      const targetData = store.vehicleData()[targetType];
      if (!sourceData.vehicle || !targetData.vehicle) return;

      const { trailer: sourceTrailer, activeOrders: sourceOrders } =
        sourceData.set;
      const { trailer: targetTrailer, activeOrders: targetOrders } =
        targetData.set;
      if (!sourceTrailer.value || !targetTrailer.value) return;

      if (
        action === SwapSetAction.SWAP &&
        !SwapHelper.canSwapTrailers(
          sourceData.vehicle,
          sourceTrailer.value,
          targetData.vehicle,
          targetTrailer.value,
        )
      ) {
        return;
      }

      const sourceSwapIndex = SwapHelper.getSwapPointIndex(sourceData.route);
      const targetSwapIndex = SwapHelper.getSwapPointIndex(targetData.route);
      const isSwap = action === SwapSetAction.SWAP;

      const sourceBeginning = sourceData.route.slice(0, sourceSwapIndex + 1);
      const targetBeginning = targetData.route.slice(0, targetSwapIndex + 1);

      const sourceTail = sourceData.route
        .slice(sourceSwapIndex + 1)
        .filter((point) => !point.isSwapPoint);
      const targetTail = targetData.route
        .slice(targetSwapIndex + 1)
        .filter((point) => !point.isSwapPoint);

      const sourceInitialMap = SwapHelper.getVehicleInitialRouteOrderMap(
        sourceData.vehicle,
        store.isDouble(),
        sourceData.swapPointUuid,
      );
      const initialRouteOrderMap = SwapHelper.getVehicleInitialRouteOrderMap(
        targetData.vehicle,
        store.isDouble(),
        targetData.swapPointUuid,
      );

      const shouldKeep = (
        point: SwapRoutePoint,
        orders: SwapSetData<Order>[],
        initialMap: Map<string, number>,
      ) => {
        if (isSwap) {
          const orderUuid = SwapHelper.getPointOrderUuid(point);
          const order = orders.find(({ value }) => value?.uuid === orderUuid);

          return order && order.action !== SwapSetAction.NONE;
        }

        return initialMap.has(SwapHelper.getPointUuid(point));
      };

      const [keptSourceTail, swappedSourceTail] = partition(
        sourceTail,
        (point) => shouldKeep(point, sourceOrders, sourceInitialMap),
      );
      const [keptTargetTail, swappedTargetTail] = partition(
        targetTail,
        (point) => shouldKeep(point, targetOrders, initialRouteOrderMap),
      );

      const newSourceRoute = isSwap
        ? [...sourceBeginning, ...keptSourceTail, ...swappedTargetTail]
        : SwapHelper.restorePointsByInitialMap(
            [...sourceBeginning, ...keptSourceTail],
            swappedTargetTail,
            sourceInitialMap,
          );

      const newTargetRoute = isSwap
        ? [...targetBeginning, ...keptTargetTail, ...swappedSourceTail]
        : SwapHelper.restorePointsByInitialMap(
            [...targetBeginning, ...keptTargetTail],
            swappedSourceTail,
            initialRouteOrderMap,
          );

      const transformOrders = (
        sourceOrders: SwapSetData<Order>[],
        targetOrders: SwapSetData<Order>[],
      ) => {
        if (isSwap) {
          return [
            ...sourceOrders.filter(
              ({ action }) => action !== SwapSetAction.NONE,
            ),
            ...targetOrders
              .filter(({ action }) => action === SwapSetAction.NONE)
              .map((order) => ({ ...order, action: SwapSetAction.TRANSFER })),
          ];
        }

        return sourceOrders.map((order) =>
          order.action === SwapSetAction.TRANSFER
            ? { ...order, action: SwapSetAction.NONE }
            : order,
        );
      };

      const newSourceOrders = transformOrders(sourceOrders, targetOrders);
      const newTargetOrders = transformOrders(targetOrders, sourceOrders);

      const updateTrailer = (
        sourceTrailer: SwapSetData<Fleet>,
        targetTrailer: SwapSetData<Fleet>,
      ) => ({
        ...sourceTrailer,
        action,
        initialData: isSwap ? sourceTrailer.value : targetTrailer.value,
        value: targetTrailer.value,
      });

      const newSourceSet = {
        ...sourceData.set,
        trailer: updateTrailer(sourceTrailer, targetTrailer),
      };
      const newTargetSet = {
        ...targetData.set,
        trailer: updateTrailer(targetTrailer, sourceTrailer),
      };

      store._patchFromVehicleData(sourceType, {
        route: newSourceRoute,
        set: {
          ...newSourceSet,
          activeOrders: SwapHelper.syncActiveOrders(
            newSourceOrders,
            newSourceRoute,
            store.availableOrders(),
            newSourceSet,
          ),
        },
      });

      store._patchFromVehicleData(targetType, {
        route: newTargetRoute,
        set: {
          ...newTargetSet,
          activeOrders: SwapHelper.syncActiveOrders(
            newTargetOrders,
            newTargetRoute,
            store.availableOrders(),
            newTargetSet,
          ),
        },
      });
    },

    _deleteTrailer(type: SwapVehicleType): void {
      const vehicleData = store._getVehicleData(type);
      const { set, route } = vehicleData;
      const { value, initialData } = set.trailer;

      const initialTrailer = initialData;
      const otherType = SwapHelper.getOtherType(type);
      const otherSetTrailer = store._getVehicleData(otherType)?.set?.trailer;

      const isTransferred =
        !!initialTrailer &&
        otherSetTrailer?.value?.uuid === initialTrailer.uuid;

      const newTrailerValue = (() => {
        if (isTransferred) return null;
        if (initialTrailer && value && value.uuid !== initialTrailer.uuid) {
          return initialTrailer;
        }

        return null;
      })();

      const newTrailerAction = (() => {
        if (isTransferred) return SwapSetAction.NONE;
        if (initialTrailer) return SwapSetAction.DROP_OFF_IN_POINT;

        return SwapSetAction.NONE;
      })();

      const newTrailer: SwapSetData<Fleet> = {
        ...set.trailer,
        value: newTrailerValue,
        action: newTrailerAction,
        isManuallyAdded: false,
      };
      const newSet = {
        ...set,
        trailer: newTrailer,
      };

      store._patchFromVehicleData(type, {
        set: {
          ...newSet,
          activeOrders: SwapHelper.syncActiveOrders(
            set.activeOrders,
            route,
            store.availableOrders(),
            newSet,
          ),
        },
      });
    },

    _moveTransferredFreeDedicatedPoints(
      sourceType: SwapVehicleType,
      route: SwapRoutePoint[],
      activeOrders: SwapSet['activeOrders'],
      blockedOrdersUuid: Set<string> = new Set(),
    ): SwapRoutePoint[] {
      const targetType = SwapHelper.getOtherType(sourceType);
      const sourceData = store.vehicleData()[sourceType];
      const targetData = store.vehicleData()[targetType];
      if (!targetData.vehicle) return route;

      const initialRouteOrderMap = SwapHelper.getVehicleInitialRouteOrderMap(
        sourceData.vehicle,
        store.isDouble(),
        sourceData.swapPointUuid,
      );

      const sourceTransferredOrdersUuid = new Set(
        activeOrders
          .filter(
            ({ action, value }) =>
              action === SwapSetAction.TRANSFER && !!value?.uuid,
          )
          .map(({ value }) => value!.uuid),
      );

      const targetTransferredOrdersUuid = new Set(
        targetData.set.activeOrders
          .filter(
            ({ action, value }) =>
              action === SwapSetAction.TRANSFER &&
              !!value?.uuid &&
              !sourceTransferredOrdersUuid.has(value.uuid),
          )
          .map(({ value }) => value!.uuid),
      );

      const swapIndex = SwapHelper.getSwapPointIndex(route);

      const { trailer: sourceTrailer } = sourceData.set;
      const { trailer: targetTrailer } = targetData.set;

      const isTrailerTransferred =
        sourceTrailer.action === SwapSetAction.TRANSFER &&
        !sourceTrailer.value &&
        targetTrailer.action === SwapSetAction.TRANSFER &&
        !!targetTrailer.value;

      if (isTrailerTransferred) {
        const afterSwapOrdersUuid = SwapHelper.getOrdersUuid(
          route.slice(swapIndex + 1),
        );

        for (const uuid of afterSwapOrdersUuid) {
          targetTransferredOrdersUuid.add(uuid);
        }
      }

      const isSwapped = (trailer: SwapSetData<Fleet>) =>
        trailer.action === SwapSetAction.SWAP &&
        !!trailer.value?.uuid &&
        trailer.value.uuid !== trailer.initialData?.uuid;

      const isSwappedTrailers =
        isSwapped(sourceTrailer) && isSwapped(targetTrailer);

      if (isSwappedTrailers) {
        for (const point of route) {
          const pointUuid = SwapHelper.getPointUuid(point);
          const orderUuid = SwapHelper.getPointOrderUuid(point);

          if (
            !point.isSwapPoint &&
            initialRouteOrderMap.has(pointUuid) &&
            orderUuid
          ) {
            targetTransferredOrdersUuid.add(orderUuid);
          }
        }
      }
      if (!targetTransferredOrdersUuid.size) return route;

      const transferredPoints: SwapRoutePoint[] = [];
      const newSourceRoute: SwapRoutePoint[] = [];

      for (const [index, point] of route.entries()) {
        const orderUuid = SwapHelper.getPointOrderUuid(point);

        if (
          index > swapIndex &&
          !point.isSwapPoint &&
          !blockedOrdersUuid.has(orderUuid ?? '') &&
          targetTransferredOrdersUuid.has(orderUuid ?? '')
        ) {
          transferredPoints.push(point);
        } else {
          newSourceRoute.push(point);
        }
      }
      if (!transferredPoints.length) return route;

      const existingTargetPointsUuid = new Set(
        targetData.route.map((point) => SwapHelper.getPointUuid(point)),
      );
      const uniqueTransferredPoints = transferredPoints.filter(
        (point) =>
          !existingTargetPointsUuid.has(SwapHelper.getPointUuid(point)),
      );

      const isTransferredTargetPoint = (point: SwapRoutePoint): boolean => {
        if (point.isSwapPoint) return false;

        const orderUuid = SwapHelper.getPointOrderUuid(point);
        const pointUuid = SwapHelper.getPointUuid(point);

        return (
          targetTransferredOrdersUuid.has(orderUuid ?? '') &&
          initialRouteOrderMap.has(pointUuid)
        );
      };

      const existingTransferredPoints: SwapRoutePoint[] = [];
      const targetRouteWithoutTransferredPoints: SwapRoutePoint[] = [];

      for (const point of targetData.route) {
        if (isTransferredTargetPoint(point)) {
          existingTransferredPoints.push(point);
        } else {
          targetRouteWithoutTransferredPoints.push(point);
        }
      }

      if (
        !uniqueTransferredPoints.length &&
        existingTransferredPoints.length === targetData.route.length
      ) {
        return newSourceRoute;
      }

      const sortedTransferredPoints = orderBy(
        uniqBy(
          [...existingTransferredPoints, ...uniqueTransferredPoints],
          (point) => SwapHelper.getPointUuid(point),
        ),
        (point) =>
          initialRouteOrderMap.get(SwapHelper.getPointUuid(point)) ?? Infinity,
        'asc',
      );

      const firstTransferredIndex = targetData.route.findIndex((point) =>
        isTransferredTargetPoint(point),
      );
      const insertTransferredIndex =
        firstTransferredIndex === -1
          ? targetRouteWithoutTransferredPoints.length
          : Math.min(
              firstTransferredIndex,
              targetRouteWithoutTransferredPoints.length,
            );

      const newTargetRoute = cloneDeep(targetRouteWithoutTransferredPoints);
      newTargetRoute.splice(
        insertTransferredIndex,
        0,
        ...sortedTransferredPoints,
      );

      const targetOrders = cloneDeep(targetData.set.activeOrders);
      const uniqueUpdateOrdersUuid = new Set(
        uniqueTransferredPoints
          .map((point) => SwapHelper.getPointOrderUuid(point))
          .filter((uuid) => !!uuid),
      );

      for (const orderUuid of uniqueUpdateOrdersUuid) {
        const orderIndex = targetOrders.findIndex(
          ({ value }) => value?.uuid === orderUuid,
        );
        if (orderIndex !== -1) {
          targetOrders[orderIndex] = {
            ...targetOrders[orderIndex],
            action: SwapSetAction.TRANSFER,
          };
          continue;
        }

        const transferredOrder = store
          .availableOrders()
          .find(({ uuid }) => uuid === orderUuid);
        if (transferredOrder) {
          targetOrders.push({
            value: transferredOrder,
            action: SwapSetAction.TRANSFER,
          });
        }
      }

      const sourceOrdersUuidBeforeSwap = SwapHelper.getOrdersUuid(
        newSourceRoute.slice(0, swapIndex),
      );

      const normalizedTargetOrders = targetOrders.map((order) => {
        const orderUuid = order.value?.uuid;

        if (
          order.action === SwapSetAction.TRANSFER &&
          orderUuid &&
          !sourceOrdersUuidBeforeSwap.has(orderUuid)
        ) {
          return { ...order, action: SwapSetAction.NONE };
        }

        return order;
      });

      store._patchFromVehicleData(targetType, {
        route: newTargetRoute,
        set: {
          ...targetData.set,
          activeOrders: SwapHelper.syncActiveOrders(
            normalizedTargetOrders,
            newTargetRoute,
            store.availableOrders(),
            targetData.set,
          ),
        },
      });

      return newSourceRoute;
    },

    updateRouteOrder(
      type: SwapVehicleType,
      dragPoint: SwapRoutePoint,
      targetPoint?: SwapRoutePoint,
    ): void {
      const data = store.vehicleData()[type];
      const {
        route,
        vehicle,
        swapPointUuid,
        set,
        unassignedPoints,
        trailerPoints,
      } = data;

      const updatedRoute = SwapHelper.reorderRoutePoints(
        route,
        dragPoint,
        targetPoint,
      );
      if (!updatedRoute.length) return;

      if (
        dragPoint.isSwapPoint &&
        [SwapSetAction.TRANSFER, SwapSetAction.SWAP].includes(
          set.trailer.action,
        )
      ) {
        const swapIndex = SwapHelper.getSwapPointIndex(route);
        const newSwapIndex = SwapHelper.getSwapPointIndex(updatedRoute);

        if (newSwapIndex < swapIndex) {
          const ordersBeforeSwap = SwapHelper.getOrdersUuid(
            route.slice(0, swapIndex),
          );
          const ordersBeforeNewSwap = SwapHelper.getOrdersUuid(
            updatedRoute.slice(0, newSwapIndex),
          );

          const hasFullTransferredOrder = Array.from(ordersBeforeSwap).some(
            (orderUuid) => {
              if (ordersBeforeNewSwap.has(orderUuid)) return false;

              return updatedRoute
                .slice(newSwapIndex + 1)
                .some(
                  (point) =>
                    SwapHelper.getPointOrderUuid(point) === orderUuid &&
                    SwapHelper.isDedicated(point),
                );
            },
          );

          if (hasFullTransferredOrder) {
            const targetType = SwapHelper.getOtherType(type);

            if (set.trailer.action === SwapSetAction.TRANSFER) {
              this._revertTransferredTrailer(targetType);
            } else {
              this._swapTrailer(type, SwapSetAction.NONE);
            }

            this.updateRouteOrder(type, dragPoint, targetPoint);
            return;
          }
        }
      }

      const initialRouteOrderMap = SwapHelper.getVehicleInitialRouteOrderMap(
        vehicle,
        store.isDouble(),
        swapPointUuid,
      );
      const sortedRoute = SwapHelper.sortDedicatedBlocks(
        updatedRoute,
        initialRouteOrderMap,
      );

      const { newRoute, newUnassigned } = SwapHelper.unassignFreeDedicatedPoint(
        sortedRoute,
        set.activeOrders,
        unassignedPoints,
        initialRouteOrderMap,
      );
      const { newRoute: routeAfterTrailer, newTrailerPoints } =
        SwapHelper.unassignFreeDedicatedTrailerPoint(
          newRoute,
          set.activeOrders,
          trailerPoints,
          set.trailer.action,
          initialRouteOrderMap,
        );

      const targetType = SwapHelper.getOtherType(type);
      const targetSet = store.vehicleData()[targetType].set;

      const swapIndex = SwapHelper.getSwapPointIndex(routeAfterTrailer);
      const routeOrdersUuid = SwapHelper.getOrdersUuid(routeAfterTrailer);
      const beforeSwapOrdersUuid = SwapHelper.getOrdersUuid(
        routeAfterTrailer.slice(0, swapIndex),
      );

      const blockedTransferredOrdersUuid = new Set(
        targetSet.activeOrders
          .filter(
            ({ value, action }) =>
              action === SwapSetAction.TRANSFER &&
              value?.uuid &&
              routeOrdersUuid.has(value.uuid) &&
              !beforeSwapOrdersUuid.has(value.uuid),
          )
          .map(({ value }) => value!.uuid),
      );

      const finalRoute = dragPoint.isSwapPoint
        ? this._moveTransferredFreeDedicatedPoints(
            type,
            routeAfterTrailer,
            set.activeOrders,
            blockedTransferredOrdersUuid,
          )
        : routeAfterTrailer;

      const newActiveOrders = set.activeOrders.map(({ value, action }) =>
        action === SwapSetAction.DROP_OFF_ON_SEMI_TRAILER &&
        value?.uuid &&
        !routeOrdersUuid.has(value.uuid)
          ? { ...{ value, action }, action: SwapSetAction.NONE }
          : { value, action },
      );

      store._patchFromVehicleData(type, {
        route: finalRoute,
        unassignedPoints: newUnassigned,
        trailerPoints: newTrailerPoints,
        set: {
          ...set,
          activeOrders: SwapHelper.syncActiveOrders(
            newActiveOrders,
            finalRoute,
            store.availableOrders(),
            set,
          ),
        },
      });

      const revertedOrders = new Set([
        ...SwapHelper.getRevertOrders(
          finalRoute,
          newActiveOrders,
          targetSet.activeOrders,
        ),
        ...blockedTransferredOrdersUuid,
      ]);

      for (const uuid of revertedOrders) {
        this.updateOrderAction(type, uuid, SwapSetAction.NONE);
        this.updateOrderAction(targetType, uuid, SwapSetAction.NONE);
      }

      const enforceDrop = (type: SwapVehicleType) => {
        const data = store.vehicleData()[type];
        const swapIndex = SwapHelper.getSwapPointIndex(data.route);

        if (
          swapIndex !== -1 &&
          swapIndex < data.route.length - 1 &&
          data.set.trailer.action === SwapSetAction.DROP_OFF_IN_POINT
        ) {
          this._dropOffTrailer(type);
        }
      };

      enforceDrop(type);
      enforceDrop(targetType);
    },

    regenerateSwapPointUuids(): void {
      const vehicleSwapPointUuid = uuidv7();
      const otherVehicleSwapPointUuid = uuidv7();

      const { route: vehicleRoute } = SwapHelper.getVehicleRoute(
        store.vehicle(),
        store.isDouble(),
        vehicleSwapPointUuid,
      );

      const { route: otherVehicleRoute } = SwapHelper.getVehicleRoute(
        store.otherVehicle(),
        store.isDouble(),
        otherVehicleSwapPointUuid,
      );

      patchState(store, {
        vehicleSwapPointUuid,
        otherVehicleSwapPointUuid,
        vehicleRoute,
        otherVehicleRoute,
      });
    },
  })),
  withHooks((store) => ({
    onInit(): void {
      store._fetch$
        .pipe(
          switchMap(() => {
            const vehicle = store.vehicle();
            const otherVehicle = store.otherVehicle();

            const vehiclesUuid = uniq(
              compact([vehicle?.uuid, otherVehicle?.uuid]),
            );
            const trailersUuid = uniq(
              compact([
                vehicle?.assignedTrailer?.uuid,
                otherVehicle?.assignedTrailer?.uuid,
              ]),
            );
            const orderUuids = uniq([
              ...SwapHelper.getOrdersUuid(store.vehicleRoute()),
              ...SwapHelper.getOrdersUuid(store.otherVehicleRoute()),
            ]);
            const driverUuids = uniq(
              compact([
                ...compact(vehicle?.drivers.map((driver) => driver.uuid)),
                ...compact(otherVehicle?.drivers.map((driver) => driver.uuid)),
              ]),
            );

            const fetch = <T>(
              uuids: string[],
              get: (uuid: string) => Observable<T>,
            ) => {
              if (!uuids.length) return of([]);

              return forkJoin(
                uuids.map((uuid) => get(uuid).pipe(catchError(() => of(null)))),
              ).pipe(map((response) => compact(response)));
            };

            return forkJoin({
              vehicles: fetch(vehiclesUuid, (uuid) =>
                store._fleetService.getFleet(uuid),
              ),
              trailers: fetch(trailersUuid, (uuid) =>
                store._fleetService.getFleet(uuid),
              ),
              drivers: fetch(driverUuids, (uuid) =>
                store._driversService.getDriver(uuid),
              ),
              orders: fetch(orderUuids, (uuid) =>
                store._ordersService.getOrder(uuid),
              ),
            }).pipe(
              takeUntil(
                store._vehicleChanged$.pipe(
                  tap(() => store.updateFetchingState(false)),
                ),
              ),
            );
          }),
        )
        .subscribe(({ orders, drivers, vehicles, trailers }) => {
          patchState(store, {
            availableOrders: orders,
            availableDrivers: drivers,
            availableTrailers: trailers,
            vehicleSet: SwapHelper.createSet(
              vehicles.find((item) => item.uuid === store.vehicle()?.uuid) ??
                null,
              store.vehicleRoute(),
              drivers,
              trailers,
              orders,
            ),
            otherVehicleSet: SwapHelper.createSet(
              vehicles.find(
                (item) => item.uuid === store.otherVehicle()?.uuid,
              ) ?? null,
              store.otherVehicleRoute(),
              drivers,
              trailers,
              orders,
            ),
            isFetching: false,
          });

          store.updateFetchingState(false);
        });

      effect(
        () => {
          if (!store.showSwapPanel() && store.vehicle()) {
            store.reset();
          }
        },
        { injector: store._injector },
      );
    },
  })),
);
